import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, asArray } from "@/lib/api";

const empty = {
  slug: "",
  title: "",
  client: "",
  category: "Sistem Informasi",
  year: new Date().getFullYear(),
  summary: "",
  description: "",
  image_url: "",
  tags: [],
  is_featured: false,
  order_idx: 0,
};

const inputCls =
  "w-full border border-[#E4E4E7] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0369A1] focus:ring-1 focus:ring-[#0369A1]";

const AdminPortfolios = () => {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () =>
    api
      .get("/portfolios", { params: { limit: 100 } })
      .then((r) => setList(asArray(r.data)))
      .catch(() => setList([]));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing({ ...empty });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing({ ...p });
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setEditing(null);
  };

  const save = async () => {
    try {
      const payload = { ...editing, tags: editing.tags || [] };
      if (editing.id) {
        await api.put(`/admin/portfolios/${editing.id}`, payload);
        toast.success("Portfolio diperbarui");
      } else {
        await api.post("/admin/portfolios", payload);
        toast.success("Portfolio dibuat");
      }
      close();
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Gagal menyimpan");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus portfolio ini?")) return;
    try {
      await api.delete(`/admin/portfolios/${id}`);
      toast.success("Dihapus");
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-portfolios-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="kt-eyebrow">CMS</div>
          <h1 className="font-display text-3xl font-bold mt-2">Portfolio</h1>
        </div>
        <button
          onClick={openNew}
          className="kt-btn-primary"
          data-testid="admin-portfolio-add-button"
        >
          <Plus size={16} /> Tambah Portfolio
        </button>
      </div>

      <div className="kt-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] border-b border-[#E4E4E7]">
            <tr className="text-left text-xs uppercase tracking-[0.14em] text-[#52525B]">
              <th className="px-4 py-3">Order</th>
              <th className="px-4 py-3">Judul</th>
              <th className="px-4 py-3">Klien</th>
              <th className="px-4 py-3">Kategori</th>
              <th className="px-4 py-3">Tahun</th>
              <th className="px-4 py-3">Featured</th>
              <th className="px-4 py-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr
                key={p.id}
                className="border-b border-[#E4E4E7] last:border-0"
                data-testid={`admin-portfolio-row-${p.slug}`}
              >
                <td className="px-4 py-3 font-mono-kt text-[#52525B]">
                  {String(p.order_idx).padStart(2, "0")}
                </td>
                <td className="px-4 py-3 font-medium">{p.title}</td>
                <td className="px-4 py-3 text-[#52525B]">{p.client}</td>
                <td className="px-4 py-3 text-[#52525B]">{p.category}</td>
                <td className="px-4 py-3 font-mono-kt">{p.year}</td>
                <td className="px-4 py-3">
                  {p.is_featured && (
                    <Star size={16} className="text-[#DC2626] fill-[#DC2626]" />
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => openEdit(p)}
                    className="p-2 hover:bg-[#F4F4F5]"
                    aria-label="edit"
                    data-testid={`admin-portfolio-edit-${p.id}`}
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => remove(p.id)}
                    className="p-2 hover:bg-[#FEE2E2] text-[#DC2626]"
                    aria-label="delete"
                    data-testid={`admin-portfolio-delete-${p.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && editing && (
        <div
          className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4"
          data-testid="portfolio-editor-modal"
        >
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#E4E4E7]">
              <h2 className="font-display text-xl font-bold">
                {editing.id ? "Edit Portfolio" : "Tambah Portfolio"}
              </h2>
              <button
                onClick={close}
                className="p-2 hover:bg-[#F4F4F5]"
                data-testid="portfolio-editor-close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Lab label="Slug">
                <input
                  className={inputCls}
                  value={editing.slug}
                  onChange={(e) =>
                    setEditing({ ...editing, slug: e.target.value })
                  }
                  data-testid="portfolio-editor-slug"
                />
              </Lab>
              <Lab label="Judul">
                <input
                  className={inputCls}
                  value={editing.title}
                  onChange={(e) =>
                    setEditing({ ...editing, title: e.target.value })
                  }
                  data-testid="portfolio-editor-title"
                />
              </Lab>
              <Lab label="Klien">
                <input
                  className={inputCls}
                  value={editing.client}
                  onChange={(e) =>
                    setEditing({ ...editing, client: e.target.value })
                  }
                />
              </Lab>
              <Lab label="Kategori">
                <input
                  className={inputCls}
                  value={editing.category}
                  onChange={(e) =>
                    setEditing({ ...editing, category: e.target.value })
                  }
                />
              </Lab>
              <Lab label="Tahun">
                <input
                  type="number"
                  className={inputCls}
                  value={editing.year}
                  onChange={(e) =>
                    setEditing({ ...editing, year: Number(e.target.value) })
                  }
                />
              </Lab>
              <Lab label="Urutan">
                <input
                  type="number"
                  className={inputCls}
                  value={editing.order_idx}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      order_idx: Number(e.target.value),
                    })
                  }
                />
              </Lab>
              <Lab label="URL Gambar" className="md:col-span-2">
                <input
                  className={inputCls}
                  value={editing.image_url}
                  onChange={(e) =>
                    setEditing({ ...editing, image_url: e.target.value })
                  }
                />
              </Lab>
              <Lab label="Ringkasan" className="md:col-span-2">
                <textarea
                  rows={2}
                  className={inputCls}
                  value={editing.summary}
                  onChange={(e) =>
                    setEditing({ ...editing, summary: e.target.value })
                  }
                />
              </Lab>
              <Lab label="Deskripsi" className="md:col-span-2">
                <textarea
                  rows={4}
                  className={inputCls}
                  value={editing.description}
                  onChange={(e) =>
                    setEditing({ ...editing, description: e.target.value })
                  }
                />
              </Lab>
              <Lab label="Tags (pisah koma)" className="md:col-span-2">
                <input
                  className={inputCls}
                  value={(editing.tags || []).join(", ")}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      tags: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </Lab>
              <label className="md:col-span-2 inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editing.is_featured}
                  onChange={(e) =>
                    setEditing({ ...editing, is_featured: e.target.checked })
                  }
                  data-testid="portfolio-editor-featured"
                />
                Featured (tampil di Beranda)
              </label>
            </div>
            <div className="p-6 border-t border-[#E4E4E7] flex justify-end gap-3">
              <button
                onClick={close}
                className="kt-btn-outline"
                data-testid="portfolio-editor-cancel"
              >
                Batal
              </button>
              <button
                onClick={save}
                className="kt-btn-primary"
                data-testid="portfolio-editor-save"
              >
                Simpan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Lab = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#18181B] mb-1.5">
      {label}
    </span>
    {children}
  </label>
);

export default AdminPortfolios;
